/**
 * Condition health + condition watching — shared, dependency-free.
 *
 * Two jobs, one file, because they are the same judgment read two ways:
 *
 *   1. HEALTH (C3) — given a condition's typed binding and its last reported
 *      reading, what state is it actually in, and how stale is that answer?
 *      The app renders this per commitment ("remains committed while X, Y, Z
 *      hold") and rolls it up to a portfolio line.
 *   2. WATCHING (C2) — which conditions have moved far enough from their
 *      stated bound that a human should be asked about them? Those become
 *      review_due-style candidates on the existing docket.
 *
 * Keeping both here is what stops the two from drifting: a strip that says
 * "holding" while the watcher proposes "in question" would be worse than
 * either alone. The database view (condition_health, migration 699)
 * deliberately does NOT compute breach for the same reason.
 *
 * GOVERNANCE (P-AGENT-1, the reviewDueCore.ts posture verbatim): detection
 * proposes; authority disposes. Nothing here moves a condition's status,
 * trips a term, reopens a decision, or answers a signal. A breached threshold
 * produces a CANDIDATE that says so in its own copy; `set_condition_status`
 * stays a human RPC.
 *
 * Imported by BOTH runtimes (the intelligenceCores.ts contract):
 *   - the Supabase edge function at supabase/functions/ambient (Deno), and
 *   - the app + vitest via src/lib/conditionObjects.ts.
 * So it MUST stay dependency-free: zero imports, no Deno or browser globals.
 */

export const CONDITION_WATCH_AGENT_TYPE = 'review_due';

/** Findings inserted per run, on top of review_due's own cap. */
export const CONDITION_WATCH_MAX_FINDINGS_PER_RUN = 10;

/**
 * A live condition nobody has looked at for this long is stale. The PRD's
 * success metric is cadence-relative ("median staleness < the workspace's
 * review cadence"), so the caller may pass its own; this is the fallback when
 * no cadence is configured, and matches the 30-day escalation boundary
 * reviewDueCore already uses for a fact left waiting.
 */
export const CONDITION_STALE_AFTER_DAYS = 30;

export type ConditionKind = 'assumption' | 'threshold' | 'dependency' | 'external_signal';
export type ConditionStatus = 'holding' | 'in_question' | 'broken' | 'retired';
export type ThresholdComparator = 'lt' | 'lte' | 'gt' | 'gte' | 'eq' | 'ne';

export type ConditionWatchFindingType =
  | 'condition_threshold_breached'
  | 'condition_evaluation_stale';

export interface ThresholdBinding {
  comparator: ThresholdComparator;
  value: number;
  unit?: string;
  sourceLabel?: string;
}

export interface ConditionInput {
  id: string;
  kind: ConditionKind;
  status: ConditionStatus;
  statement: string;
  /** Decision title or item title — whatever the condition hangs on. */
  subjectTitle: string | null;
  subjectKind: 'decision' | 'commitment';
  /** The item the condition implicates, when it has one — findings cite it. */
  itemId: string | null;
  /** Parsed from condition_objects.binding; null for kinds that carry none. */
  threshold: ThresholdBinding | null;
  lastObservedValue: number | null;
  /** ISO timestamp of the reading behind lastObservedValue. */
  lastObservedAt: string | null;
  lastObservedBasis: string | null;
  /** ISO timestamp a human last judged this claim. */
  lastEvaluatedAt: string | null;
}

// ── Health ───────────────────────────────────────────────────────────────────

/**
 * What the record actually says about one condition right now.
 *
 * `breached` is a fact about numbers, not a status: a threshold whose last
 * reading is outside its bound is breached even while its status is still
 * `holding`, because only a human moves the status. That gap — breached but
 * still holding — is exactly what C2 puts on the docket, and what C3 shows as
 * "needs a look".
 */
export interface ConditionHealth {
  /** True when a threshold's last reading is outside its stated bound. */
  breached: boolean;
  /** Whole days since a human last evaluated; null when never evaluated. */
  daysSinceEvaluated: number | null;
  stale: boolean;
  /** Short chip label: "Holding", "In question", "Broken", "Retired". */
  label: string;
  /** One plain sentence for detail rows. No invented specificity. */
  sentence: string;
  /** Whether a human should look at this now. */
  needsLook: boolean;
}

export const CONDITION_KIND_LABELS: Record<ConditionKind, string> = {
  assumption: 'Assumption',
  threshold: 'Threshold',
  dependency: 'Dependency',
  external_signal: 'External signal',
};

export const CONDITION_STATUS_LABELS: Record<ConditionStatus, string> = {
  holding: 'Holding',
  in_question: 'In question',
  broken: 'Broken',
  retired: 'Retired',
};

const COMPARATOR_WORDS: Record<ThresholdComparator, string> = {
  lt: 'below',
  lte: 'at or below',
  gt: 'above',
  gte: 'at or above',
  eq: 'exactly',
  ne: 'anything other than',
};

const MS_PER_DAY = 86_400_000;

/** A condition is live while a human judgment about it is still possible. */
export function isConditionLive(status: ConditionStatus): boolean {
  return status === 'holding' || status === 'in_question';
}

/** Mirrors the transitions set_condition_status accepts — nothing else is legal. */
const LEGAL_CONDITION_TRANSITIONS: Record<ConditionStatus, readonly ConditionStatus[]> = {
  holding: ['in_question', 'broken', 'retired'],
  in_question: ['holding', 'broken', 'retired'],
  broken: ['retired'],
  retired: [],
};

export function canTransitionCondition(from: ConditionStatus, to: ConditionStatus): boolean {
  return LEGAL_CONDITION_TRANSITIONS[from]?.includes(to) ?? false;
}

/** Does `observed` satisfy the bound the condition was written with? */
export function thresholdHolds(binding: ThresholdBinding, observed: number): boolean {
  switch (binding.comparator) {
    case 'lt':
      return observed < binding.value;
    case 'lte':
      return observed <= binding.value;
    case 'gt':
      return observed > binding.value;
    case 'gte':
      return observed >= binding.value;
    case 'eq':
      return observed === binding.value;
    case 'ne':
      return observed !== binding.value;
    default:
      // An unknown comparator must never read as "holding": that would be the
      // system quietly asserting a claim it cannot check.
      return false;
  }
}

/** "at or above 94%" — the bound in words, for copy that a reader can check. */
export function describeThreshold(binding: ThresholdBinding): string {
  const unit = binding.unit ?? '';
  return `${COMPARATOR_WORDS[binding.comparator] ?? binding.comparator} ${binding.value}${unit}`;
}

function wholeDaysSince(iso: string | null, nowIso: string): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  const now = Date.parse(nowIso);
  if (!Number.isFinite(then) || !Number.isFinite(now)) return null;
  return Math.max(0, Math.floor((now - then) / MS_PER_DAY));
}

export interface ConditionHealthOptions {
  /** ISO timestamp — injected so judgment is testable (commitmentTerms.ts idiom). */
  now: string;
  /** Workspace review cadence in days; falls back to CONDITION_STALE_AFTER_DAYS. */
  staleAfterDays?: number;
}

export function judgeCondition(
  condition: ConditionInput,
  options: ConditionHealthOptions,
): ConditionHealth {
  const staleAfter = options.staleAfterDays ?? CONDITION_STALE_AFTER_DAYS;
  const daysSinceEvaluated = wholeDaysSince(condition.lastEvaluatedAt, options.now);
  const live = isConditionLive(condition.status);

  const breached =
    condition.kind === 'threshold' &&
    condition.threshold !== null &&
    condition.lastObservedValue !== null &&
    !thresholdHolds(condition.threshold, condition.lastObservedValue);

  // Never evaluated counts as stale for a LIVE condition — a claim nobody has
  // ever checked is the weakest thing the record can hold. A retired one is
  // simply finished.
  const stale =
    live && (daysSinceEvaluated === null || daysSinceEvaluated >= staleAfter);

  const label = CONDITION_STATUS_LABELS[condition.status];

  let sentence: string;
  if (condition.status === 'retired') {
    sentence = 'Retired — this claim is no longer part of the commitment.';
  } else if (condition.status === 'broken') {
    sentence = 'Recorded as no longer true.';
  } else if (breached && condition.threshold) {
    const reading =
      `${condition.lastObservedValue}${condition.threshold.unit ?? ''}` +
      (condition.lastObservedAt ? ` on ${condition.lastObservedAt.slice(0, 10)}` : '');
    sentence =
      `Stated as ${describeThreshold(condition.threshold)}; last reported ${reading}. ` +
      'Still recorded as ' +
      `${label.toLowerCase()} — moving it is a human call.`;
  } else if (condition.status === 'in_question') {
    sentence = 'Marked in question; no decision has been made about it yet.';
  } else if (daysSinceEvaluated === null) {
    sentence = 'Holding, but never evaluated since it was recorded.';
  } else if (stale) {
    sentence = `Holding; last looked at ${daysSinceEvaluated} days ago.`;
  } else {
    sentence = `Holding; last looked at ${daysSinceEvaluated === 0 ? 'today' : `${daysSinceEvaluated} days ago`}.`;
  }

  return {
    breached,
    daysSinceEvaluated,
    stale,
    label,
    sentence,
    needsLook: live && (breached || stale || condition.status === 'in_question'),
  };
}

// ── Rollup (C3) ──────────────────────────────────────────────────────────────

export interface ConditionRollup {
  total: number;
  live: number;
  holding: number;
  inQuestion: number;
  broken: number;
  retired: number;
  breached: number;
  stale: number;
  /** "2 of 5 conditions in question" — or null when there is nothing to say. */
  headline: string | null;
}

export function rollUpConditions(
  conditions: ConditionInput[],
  options: ConditionHealthOptions,
): ConditionRollup {
  const rollup: ConditionRollup = {
    total: conditions.length,
    live: 0,
    holding: 0,
    inQuestion: 0,
    broken: 0,
    retired: 0,
    breached: 0,
    stale: 0,
    headline: null,
  };

  for (const c of conditions) {
    const health = judgeCondition(c, options);
    if (isConditionLive(c.status)) rollup.live++;
    if (c.status === 'holding') rollup.holding++;
    if (c.status === 'in_question') rollup.inQuestion++;
    if (c.status === 'broken') rollup.broken++;
    if (c.status === 'retired') rollup.retired++;
    if (health.breached) rollup.breached++;
    if (health.stale) rollup.stale++;
  }

  const parts: string[] = [];
  if (rollup.inQuestion > 0) parts.push(`${rollup.inQuestion} in question`);
  if (rollup.broken > 0) parts.push(`${rollup.broken} broken`);
  if (rollup.breached > 0) parts.push(`${rollup.breached} past its stated bound`);
  if (rollup.stale > 0) parts.push(`${rollup.stale} not evaluated recently`);

  if (rollup.total === 0) {
    rollup.headline = null;
  } else if (parts.length === 0) {
    rollup.headline = `All ${rollup.total} condition${rollup.total === 1 ? '' : 's'} holding.`;
  } else {
    rollup.headline = `${parts.join(', ')} of ${rollup.total} condition${rollup.total === 1 ? '' : 's'}.`;
  }

  return rollup;
}

// ── Watching (C2) ────────────────────────────────────────────────────────────

export interface ConditionWatchDraft {
  findingType: ConditionWatchFindingType;
  severity: 'high' | 'medium';
  title: string;
  summary: string;
  detail: string;
  itemIds: string[];
  /**
   * Identity of the FACT, not the row — the reviewDueCore discipline. The
   * BASIS is in the key (the reading and when it was taken, or the evaluation
   * date), so a threshold re-crossed after remediation is a legitimately new
   * proposal, while an unchanged fact — even one a human dismissed — is never
   * re-proposed.
   */
  dedupKey: string;
  data: Record<string, unknown>;
  /** Ranking aid: days the fact has been sitting. */
  overdueDays: number;
}

export interface ConditionWatchInput {
  conditions: ConditionInput[];
  /** ISO timestamp — injected so judgment is testable. */
  now: string;
  staleAfterDays?: number;
}

const PROPOSAL_ONLY =
  'This is a proposal only — the condition has not been moved, nothing has been reopened, and no commitment has changed. The decision stays with you.';

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/**
 * Every condition a human should be asked about, most urgent first.
 *
 * Two facts qualify, and only two:
 *   - a threshold whose last reported reading is outside its stated bound
 *     while the condition is still recorded as holding or in question;
 *   - a live condition nobody has evaluated within the review cadence.
 *
 * A condition already recorded as `broken` produces nothing: the human has
 * already answered. `retired` produces nothing at all.
 */
export function detectConditionCandidates(input: ConditionWatchInput): ConditionWatchDraft[] {
  const drafts: ConditionWatchDraft[] = [];
  const options: ConditionHealthOptions = {
    now: input.now,
    staleAfterDays: input.staleAfterDays,
  };

  for (const c of input.conditions) {
    if (!isConditionLive(c.status)) continue;
    const health = judgeCondition(c, options);
    const subject = c.subjectTitle?.trim() || (c.subjectKind === 'decision' ? 'a decision' : 'a plan commitment');
    const itemIds = c.itemId ? [c.itemId] : [];

    if (health.breached && c.threshold && c.lastObservedValue !== null) {
      const observedOn = c.lastObservedAt?.slice(0, 10) ?? 'an unrecorded date';
      const reading = `${c.lastObservedValue}${c.threshold.unit ?? ''}`;
      const bound = describeThreshold(c.threshold);
      const overdue = wholeDaysSince(c.lastObservedAt, input.now) ?? 0;
      drafts.push({
        findingType: 'condition_threshold_breached',
        severity: 'high',
        title: truncate(`Condition past its bound on “${subject}”`, 200),
        summary:
          `“${truncate(c.statement, 120)}” was stated as ${bound}; the last reported reading was ${reading} on ${observedOn}.`,
        detail:
          `“${subject}” carries the condition “${truncate(c.statement, 300)}”, stated as ${bound}` +
          (c.threshold.sourceLabel ? ` (${truncate(c.threshold.sourceLabel, 120)})` : '') +
          `. The reading reported on ${observedOn} was ${reading}` +
          (c.lastObservedBasis ? ` — ${truncate(c.lastObservedBasis, 160)}` : '') +
          `. The condition is still recorded as ${health.label.toLowerCase()}. ` +
          PROPOSAL_ONLY,
        itemIds,
        dedupKey: `condition:${c.id}:breach:${c.lastObservedValue}:${c.lastObservedAt ?? 'unknown'}`,
        data: {
          condition_id: c.id,
          condition_kind: c.kind,
          condition_status: c.status,
          comparator: c.threshold.comparator,
          bound_value: c.threshold.value,
          observed_value: c.lastObservedValue,
          observed_at: c.lastObservedAt,
        },
        overdueDays: overdue,
      });
      continue;
    }

    if (health.stale) {
      const days = health.daysSinceEvaluated;
      drafts.push({
        findingType: 'condition_evaluation_stale',
        severity: 'medium',
        title: truncate(`Condition not checked recently on “${subject}”`, 200),
        summary:
          days === null
            ? `“${truncate(c.statement, 120)}” has never been evaluated since it was recorded.`
            : `“${truncate(c.statement, 120)}” was last looked at ${days} days ago.`,
        detail:
          `“${subject}” still rests on the ${CONDITION_KIND_LABELS[c.kind].toLowerCase()} ` +
          `“${truncate(c.statement, 300)}”. ` +
          (days === null
            ? 'Nobody has evaluated it since it was recorded. '
            : `Nobody has evaluated it for ${days} days. `) +
          'Confirm it still holds, or say what changed. ' +
          PROPOSAL_ONLY,
        itemIds,
        dedupKey: `condition:${c.id}:stale:${c.lastEvaluatedAt?.slice(0, 10) ?? 'never'}`,
        data: {
          condition_id: c.id,
          condition_kind: c.kind,
          condition_status: c.status,
          last_evaluated_at: c.lastEvaluatedAt,
          days_since_evaluated: days,
        },
        overdueDays: days ?? 0,
      });
    }
  }

  return drafts.sort(
    (a, b) =>
      (a.severity === b.severity ? 0 : a.severity === 'high' ? -1 : 1) ||
      b.overdueDays - a.overdueDays,
  );
}
